// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import {
  TrendingUp, TrendingDown, DollarSign, ShoppingCart,
  FlaskConical, Package, AlertTriangle, CheckCircle2, Clock,
} from 'lucide-react';
import api from '../utils/api';
import { revenueData, productionByStyle, recentActivity } from '../data/mockData';

const KPICard = ({ title, value, change, icon: Icon, prefix = '' }) => {
  const isPositive = change >= 0;
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-lg bg-cyan-50 flex items-center justify-center">
          <Icon size={20} className="text-[#0891B2]" />
        </div>
        <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${
          isPositive ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          {isPositive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
          {Math.abs(change)}%
        </span>
      </div>
      <p className="text-2xl font-bold text-gray-900">{prefix}{typeof value === 'number' ? value.toLocaleString() : value}</p>
      <p className="text-sm text-gray-500 mt-1">{title}</p>
    </div>
  );
};

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await api.get('/dashboard/');
        setData(res.data);
      } catch (err) {
        console.error('Failed to fetch dashboard', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const kpis = data?.kpis || { totalRevenue: 0, revenueChange: 0, totalOrders: 0, ordersChange: 0, activeBatches: 0, batchesChange: 0, inventoryValue: 0, inventoryChange: 0 };
  const activeBatches = data?.activeBatches || [];
  const lowStockItems = data?.lowStockItems || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0891B2]"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">Welcome back. Here's what's happening across your operations.</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPICard title="Total Revenue (YTD)" value={kpis.totalRevenue} change={kpis.revenueChange} icon={DollarSign} prefix="$" />
        <KPICard title="Total Orders (YTD)" value={kpis.totalOrders} change={kpis.ordersChange} icon={ShoppingCart} />
        <KPICard title="Active Batches" value={kpis.activeBatches} change={kpis.batchesChange} icon={FlaskConical} />
        <KPICard title="Inventory Value" value={kpis.inventoryValue} change={kpis.inventoryChange} icon={Package} prefix="$" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Revenue & Orders</h3>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Last 12 months</span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={revenueData}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0891B2" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#0891B2" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" tickFormatter={(v) => `$${v / 1000}k`} />
              <Tooltip formatter={(val) => [`$${val.toLocaleString()}`, 'Revenue']} />
              <Area type="monotone" dataKey="revenue" stroke="#0891B2" fill="url(#revGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Production by Style</h3>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie 
                data={productionByStyle} 
                cx="50%"
                cy="50%"
                innerRadius={50} 
                outerRadius={75} 
                dataKey="value"
                nameKey="name"
                paddingAngle={3}
              >
                {productionByStyle.map((entry, i) => (
                  <Cell key={`cell-${i}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip formatter={(val) => [`${val}%`, 'Share']} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-3 mt-2">
            {productionByStyle.map((s) => (
              <div key={s.name} className="flex items-center gap-1.5 text-xs text-gray-600">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                {s.name}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Active Batches</h3>
            <span className="text-xs text-gray-500">{activeBatches.length} active</span>
          </div>
          <div className="space-y-3">
            {activeBatches.slice(0, 5).map((batch) => (
              <div key={batch.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{batch.name || 'Unnamed Batch'}</p>
                  <p className="text-xs text-gray-500">{batch.batchSize || 0} {batch.batchUnit || 'BBL'} • {batch.style || 'Unknown Style'}</p>
                </div>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${
                  batch.status === 'Brewing' ? 'bg-orange-100 text-orange-700' :
                  batch.status === 'Fermenting' ? 'bg-blue-100 text-blue-700' :
                  batch.status === 'Conditioning' ? 'bg-purple-100 text-purple-700' :
                  batch.status === 'Planning' ? 'bg-gray-100 text-gray-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {batch.status}
                </span>
              </div>
            ))}
            {activeBatches.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-4">No active batches</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Low Stock Alerts</h3>
            <AlertTriangle size={16} className="text-amber-500" />
          </div>
          <div className="space-y-3">
            {lowStockItems.map((item) => (
              <div key={item.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <div>
                  <p className="text-sm font-medium text-gray-900">{item.name}</p>
                  <p className="text-xs text-gray-500">{item.quantity} {item.unit} remaining</p>
                </div>
                <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-amber-50 text-amber-700">
                  Reorder: {item.reorderPoint} {item.unit}
                </span>
              </div>
            ))}
            {lowStockItems.length === 0 && (
              <div className="flex items-center gap-2 text-sm text-green-600 py-4">
                <CheckCircle2 size={16} /> All stock levels are healthy
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Recent Activity</h3>
            <Clock size={16} className="text-gray-400" />
          </div>
          <div className="space-y-3">
            {recentActivity.slice(0, 5).map((act) => (
              <div key={act.id} className="flex items-start gap-3 py-2 border-b border-gray-50 last:border-0">
                <div className="w-2 h-2 rounded-full bg-[#0891B2] mt-1.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-700">{act.message}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{act.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
